import { Box } from '@mui/system';
import { Headings, LargeText, MediumText, NormalText, SmallText } from 'components/atoms/Typography';
import { Cart, Payment } from 'klikni-jadi-shared-stuff';
import useTranslation from 'next-translate/useTranslation';
import cardImg from 'assets/svg/card.svg';
import cardBlack from 'assets/svg/cardBlack.svg';
import cash from 'assets/svg/cash.svg';
import cashBlue from 'assets/svg/cashBlue.svg';
import rightArrow from 'assets/svg/rightArrow.svg';
import { useEffect, useState } from 'react';
import ModalPopup from 'components/molecules/ModalPopup';
import { ClickAwayListener, Divider, TextField } from '@mui/material';
import { PrimaryButton } from 'components/atoms/Buttons';
import takeawayBackground from 'assets/svg/takeawaybackground.svg';
import CartCheckbox from 'components/CartCheckbox';
import { useCartService } from 'services/cart/useCartService';
import CartItemQuantityComponent from 'components/CartItemQuantityComponenet';
import back from 'assets/svg/back.svg';
import check from 'assets/svg/check.svg';
import warning from 'assets/svg/warning.svg';
import ItemQuantityComponent from 'components/molecules/ItemQuantityComponent';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import _, { isEmpty, values } from 'lodash';
import DividerWithText from 'components/molecules/DividerWithText';

interface IPaymentDetailsProps {
    cart?: Cart;
    paymentType?: Payment;
    onChangePaymentType?: (type) => void;
    customerChange?: string;
    onChangeCustomerChange?: (amount) => void;
    error?: boolean;
    onChangeError?: () => void;
    handleChooseCustomerChange?: () => void;
    onChooseCard?: (safeKey: string) => void;
    onlyPayOnline?: boolean;
    onlyPayCash?: boolean;
}

const PaymentDetails = ({
    cart,
    paymentType,
    onChangePaymentType,
    customerChange,
    onChangeCustomerChange,
    error,
    onChangeError,
    handleChooseCustomerChange,
    onChooseCard,
    onlyPayCash,
    onlyPayOnline,
}: IPaymentDetailsProps) => {
    const { t } = useTranslation('common');
    const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
    const [haveExactAmount, setHaveExactAmount] = useState<boolean>(null);
    const [step, setStep] = useState<number>(1);
    const [willEnterNewCard, setWillEnterNewCard] = useState<boolean>(false);
    const [selectedCard, setSelectedCard] = useState<string>(null);
    const [focusInput, setFocusInput] = useState<boolean>(false);
    const { getFinalCartPrice } = useCartService();
    const currentUser = useSelector((state: RootState) => state.currentUser);
    const { enqueueSnackbar } = useSnackbar();
    useEffect(() => {
        if (onlyPayOnline) {
            setWillEnterNewCard(true);
        }
    }, [onlyPayOnline]);
    const chooseCustomerChange = () => {
        if (customerChange && customerChange?.trim?.() !== '') {
            setOpenPaymentModal(false);
            handleChooseCustomerChange?.();
        } else {
            enqueueSnackbar(t('enter_change'), { variant: 'error' });
        }
    };

    return (
        <Box mt={4} mb={6} display="flex" flexDirection="column" position="relative" id="paymentContainer">
            <Headings.H2 sx={{ mb: 3 }}>{t('payment_details')}</Headings.H2>
            {error && (
                <Box position="absolute" top={20} right={0} display="flex" alignItems="center">
                    <img src={warning} />
                    <SmallText sx={{ ml: 1 }} semibold color="#FE8C67">
                        Select payment type
                    </SmallText>
                </Box>
            )}
            <Box
                display="flex"
                p={2}
                sx={{ border: `1px solid ${error ? '#FE8C67' : '#D9D9D9'} `, borderRadius: '5px', cursor: 'pointer' }}
                justifyContent="space-between"
                alignItems="center"
                onClick={() => setOpenPaymentModal(true)}
            >
                {paymentType === 'cash' ? (
                    <Box display="flex" alignItems="center">
                        <Box pr={2} display="flex" alignItems="center" justifyContent="center">
                            <img src={cashBlue} width="25px" />
                        </Box>
                        <Box display="flex" flexDirection="column">
                            <MediumText sx={{ color: 'primary.main' }}>{t('cash')}</MediumText>
                            <NormalText sx={{ color: 'gray' }}>{t('add_payment')}</NormalText>
                        </Box>
                    </Box>
                ) : paymentType === 'online' ? (
                    willEnterNewCard ? (
                        <Box display="flex" alignItems="center">
                            <Box pr={2} display="flex" alignItems="center" justifyContent="center">
                                <img src={cardImg} width="25px" />
                            </Box>
                            <Box display="flex" flexDirection="column">
                                <MediumText sx={{ color: 'primary.main' }}>{t('willEnterCard')}</MediumText>
                                {/* <MediumText sx={{ color: 'gray' }}>{t('add_payment')}</MediumText> */}
                            </Box>
                        </Box>
                    ) : (
                        <Box display="flex" alignItems="center">
                            <Box pr={2} display="flex" alignItems="center" justifyContent="center">
                                <img src={cardImg} width="25px" />
                            </Box>
                            <Box display="flex" flexDirection="column">
                                <MediumText sx={{ color: 'primary.main' }}>
                                    {t('will_pay_with')}: {selectedCard}{' '}
                                </MediumText>
                                {/* <MediumText sx={{ color: 'gray' }}>{t('add_payment')}</MediumText> */}
                            </Box>
                        </Box>
                    )
                ) : (
                    <Box display="flex" alignItems="center">
                        <Box pr={2} display="flex" alignItems="center" justifyContent="center">
                            <img src={cardImg} />
                        </Box>
                        <Box display="flex" flexDirection="column">
                            <MediumText sx={{ color: 'primary.main' }}>{t('choose_payment')}</MediumText>
                            <NormalText sx={{ color: 'gray' }}>{t('add_payment')}</NormalText>
                        </Box>
                    </Box>
                )}
                <Box display="flex" alignItems="center" justifyContent="center">
                    <img src={rightArrow} width="10px" />
                </Box>
            </Box>
            <ModalPopup
                maxWidth={{ xs: '100%', sm: '500px', md: '600px' }}
                minWidth={{ xs: '100%', sm: '500px', md: '600px' }}
                minHeight="auto"
                open={openPaymentModal}
                alignItems="flex-start"
                handleClose={() => {
                    setOpenPaymentModal(false);
                    setStep(1);
                }}
            >
                {step === 1 ? (
                    <Box py={5} px={2} width="100%" display="flex" flexDirection="column">
                        <Headings.H2 sx={{ mb: 3 }}>{t('payment_types')}</Headings.H2>

                        {!onlyPayCash && (
                            <Box width="100%" display="flex" flexDirection="column">
                                <Divider />
                                <Box
                                    onClick={() => {
                                        onChangePaymentType?.('online');
                                        onChangeError?.();
                                        setOpenPaymentModal(false);
                                        setStep(1);
                                        setWillEnterNewCard(true);
                                        setSelectedCard(null);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                    py={3}
                                    ml={2}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Box display="flex" alignItems="center">
                                        <img src={willEnterNewCard ? cardImg : cardBlack} width="25px" />
                                        <LargeText color={willEnterNewCard ? 'primary.main' : 'black'} sx={{ ml: 2 }}>
                                            {t('willEnterCard')}
                                        </LargeText>
                                    </Box>
                                    {willEnterNewCard && (
                                        <Box
                                            display="flex"
                                            width="35px"
                                            height="35px"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                backgroundColor: '#009DE0',
                                                borderRadius: '50px',
                                                cursor: 'pointer',
                                                ':hover': {
                                                    backgroundColor: '#009DE080',
                                                },
                                            }}
                                        >
                                            <img src={check} width="17px" />
                                        </Box>
                                    )}
                                </Box>
                                <Box pb={1.5} display="flex" flexDirection="column">
                                    {!isEmpty(currentUser?.data?.bankCards) &&
                                        _.find(currentUser?.data?.bankCards, bk => bk?.verified) && (
                                            <DividerWithText>
                                                <LargeText>{t('pay_with')}</LargeText>
                                            </DividerWithText>
                                        )}

                                    {values(currentUser?.data?.bankCards).map((bankCard, index) => {
                                        if (bankCard?.verified) {
                                            return (
                                                <Box
                                                    onClick={() => {
                                                        setSelectedCard(bankCard?.cardMasked);
                                                        setWillEnterNewCard(false);
                                                        onChangeError?.();
                                                        onChangePaymentType?.('online');
                                                        setOpenPaymentModal(false);
                                                        onChooseCard?.(bankCard?.safeKey);
                                                        setStep(1);
                                                    }}
                                                    mt={2}
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <Box ml={2} display="flex" alignItems="center">
                                                        <img
                                                            src={selectedCard === bankCard?.cardMasked ? cardImg : cardBlack}
                                                            width="25px"
                                                        />
                                                        <LargeText
                                                            color={
                                                                selectedCard === bankCard?.cardMasked ? 'primary.main' : 'black'
                                                            }
                                                            sx={{ ml: 2 }}
                                                        >
                                                            {bankCard.cardMasked}
                                                        </LargeText>
                                                    </Box>
                                                    {selectedCard === bankCard?.cardMasked && (
                                                        <Box
                                                            display="flex"
                                                            width="35px"
                                                            height="35px"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            sx={{
                                                                backgroundColor: '#009DE0',
                                                                borderRadius: '50px',
                                                                cursor: 'pointer',
                                                                ':hover': {
                                                                    backgroundColor: '#009DE080',
                                                                },
                                                            }}
                                                        >
                                                            <img src={check} width="17px" />
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        }
                                    })}
                                </Box>
                            </Box>
                        )}

                        {!onlyPayOnline && (
                            <Box width="100%" display="flex" flexDirection="column">
                                {!onlyPayCash && <Headings.H2 sx={{ mb: 4, mt: 2 }}>{t('other_payment_options')}</Headings.H2>}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    ml={2}
                                    justifyContent="space-between"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        onChangePaymentType?.('cash');
                                        onChangeError?.();
                                        setWillEnterNewCard(false);
                                        setSelectedCard(null);
                                        setStep(2);
                                    }}
                                >
                                    <Box display="flex" alignItems="center">
                                        <Box mr={2} display="flex" alignItems="center">
                                            <img src={paymentType === 'cash' ? cashBlue : cash} width="25px" />
                                        </Box>
                                        <LargeText color={paymentType === 'cash' ? 'primary.main' : 'black'}>
                                            {t('cash')}
                                        </LargeText>
                                    </Box>
                                    {paymentType === 'cash' && (
                                        <Box
                                            display="flex"
                                            width="35px"
                                            height="35px"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                backgroundColor: '#009DE0',
                                                borderRadius: '50px',
                                                cursor: 'pointer',
                                                ':hover': {
                                                    backgroundColor: '#009DE080',
                                                },
                                            }}
                                        >
                                            <img src={check} width="17px" />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box pb={5} pt={3} px={2} width="100%" display="flex" flexDirection="column">
                        <Box
                            display="flex"
                            width="40px"
                            height="40px"
                            alignItems="center"
                            justifyContent="center"
                            onClick={() => setStep(1)}
                            sx={{
                                backgroundColor: '#D9D9D980',
                                borderRadius: '50px',
                                cursor: 'pointer',
                                ':hover': {
                                    backgroundColor: '#D9D9D9',
                                },
                            }}
                        >
                            <img src={back} width="20px" />
                        </Box>
                        <Headings.H2 sx={{ my: 3 }}>{t('cash_delivery')}</Headings.H2>

                        <Box
                            // onClick={() => {
                            //     onChangePaymentType?.('online');
                            //     setTimeout(() => {
                            //         setOpenPaymentModal(false);
                            //         setStep(1);
                            //     }, 1000);
                            // }}
                            sx={{ cursor: 'pointer' }}
                            py={3}
                            ml={2}
                            display="flex"
                            alignItems="center"
                        >
                            <CartCheckbox
                                checked={haveExactAmount}
                                handleCheck={() => {
                                    setHaveExactAmount(true);
                                    onChangeCustomerChange?.(getFinalCartPrice());
                                }}
                            />
                            <Box
                                ml={2}
                                display="flex"
                                flexDirection="column"
                                onClick={() => {
                                    setHaveExactAmount(true);
                                    onChangeCustomerChange?.(getFinalCartPrice());
                                }}
                            >
                                <LargeText>
                                    {t('have_exact')} {getFinalCartPrice()} {t('mkd')}{' '}
                                </LargeText>
                                <MediumText color="gray">{t('no_change')}</MediumText>
                            </Box>
                        </Box>
                        <Divider />
                        <Box
                            // onClick={() => {
                            //     onChangePaymentType?.('online');
                            //     setTimeout(() => {
                            //         setOpenPaymentModal(false);
                            //         setStep(1);
                            //     }, 1000);
                            // }}
                            sx={{ cursor: 'pointer' }}
                            py={3}
                            ml={2}
                            display="flex"
                            alignItems="center"
                        >
                            <CartCheckbox checked={haveExactAmount === false} handleCheck={() => setHaveExactAmount(false)} />
                            <Box
                                ml={2}
                                display="flex"
                                flexDirection="column"
                                onClick={() => {
                                    setHaveExactAmount(false);
                                }}
                            >
                                <LargeText>{t('not_have_exact')}</LargeText>
                                <MediumText color="gray">
                                    {t('choose_sum')} {getFinalCartPrice()}
                                    {t('mkd')} - 2000{t('mkd')}
                                </MediumText>
                            </Box>
                        </Box>
                        {haveExactAmount === false && (
                            <ClickAwayListener onClickAway={() => setFocusInput(false)}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    onClick={() => setFocusInput(true)}
                                    justifyContent="center"
                                    sx={{
                                        border: `${focusInput ? '2px' : '1px'} solid ${focusInput ? '#009DE0' : '#D9D9D9'}`,
                                        borderRadius: '5px',
                                    }}
                                    p={1}
                                    my={2}
                                    mx={5}
                                >
                                    {/* <CartItemQuantityComponent
                                    value={customerChange || getFinalCartPrice()}
                                    minValue={getFinalCartPrice()}
                                    onChange={val => onChangeCustomerChange?.(val)}
                                />{' '} */}
                                    {/* <ItemQuantityComponent
                                    value={customerChange || getFinalCartPrice()}
                                    minValue={getFinalCartPrice()}
                                    onChange={val => onChangeCustomerChange?.(val)}
                                /> */}
                                    <TextField
                                        variant="standard"
                                        value={customerChange}
                                        fullWidth
                                        defaultValue={customerChange}
                                        type="number"
                                        InputProps={{
                                            sx: {
                                                width: '100%',
                                                fontWeight: 600,
                                                textAlign: 'center',
                                                color: 'primary.main',
                                                borderColor: 'primary.main',
                                                fontSize: { xs: '16px', md: '1em' },
                                                ':before': {
                                                    borderBottom: 'none',
                                                },

                                                ':after': {
                                                    borderBottom: 'none',
                                                },
                                                ':hover': {
                                                    borderBottom: 'none',
                                                },
                                                '&:hover:not(.Mui-disabled):before': {
                                                    borderBottom: 'none',
                                                },
                                                '&:after': {
                                                    borderBottom: 'none',
                                                },
                                                '& input[type=number]': {
                                                    '-moz-appearance': 'textfield',
                                                    textAlign: 'center',
                                                },
                                                '& input[type=number]::-webkit-outer-spin-button': {
                                                    '-webkit-appearance': 'none',
                                                    margin: 0,
                                                },
                                                '& input[type=number]::-webkit-inner-spin-button': {
                                                    '-webkit-appearance': 'none',
                                                    margin: 0,
                                                },
                                            },
                                        }}
                                        onChange={event => onChangeCustomerChange?.(event.target.value)}
                                    />
                                </Box>
                            </ClickAwayListener>
                        )}
                        <PrimaryButton onClick={chooseCustomerChange} sx={{ mt: 2 }}>
                            {t('choose')}
                        </PrimaryButton>
                    </Box>
                )}
            </ModalPopup>
        </Box>
    );
};

export default PaymentDetails;
